import axios from 'axios';
import { baseAutomationUrl } from 'src/config';
import { getAccessToken, getRealmName } from 'src/routes/AuthContext';
import { PBJ_REQUEST_TIMEOUT_MS, pbjPath } from './constants';
import { IPbjExportRequest } from './types';
import { getClientIpHeader } from 'src/services/clientIpService/getClientIp';

/**
 * `GET /pbj` — the quarterly CMS Payroll-Based Journal submission for the whole enterprise.
 *
 * Two deliberate departures from the SLM / UB04 / Census downloads next to it:
 *
 * 1. **`facility_uuid` is opt-in, not the default.** Those three always scope to the page's
 *    facility selector. PBJ does not: CMS puts one facilityId per XML but accepts a zip of many
 *    under 5 MB, and a measured 100-facility quarter zips to ~1.6 MB. One archive is both valid
 *    and what a chain actually files — nobody wants to click this a hundred times. The parameter
 *    is sent only when the card passes `facilityUuid`, which it does only when the user picks
 *    "This facility only" for a spot-check. This function never reads the selector itself: if it
 *    did, `localStorage.facilityId` (always exactly one uuid, never an "all" sentinel) would make
 *    every filing single-facility.
 * 2. **An explicit timeout.** The shared interceptor sets none, so without this a hang would wait
 *    forever. Scoped to this call only; a global timeout would change every other request.
 *
 * Follows `getPullServiceLog` for auth: this hits `baseAutomationUrl` with raw axios and an
 * explicit bearer header, not the `src/services/interceptor.tsx` instance (that one is bound to
 * `baseApiUrl`). The realm travels as the `enterprise_name` query parameter.
 */
export const getPbjExport = async (request: IPbjExportRequest) => {
  const token = await getAccessToken();
  const realm = getRealmName() ?? '';

  const url = `${baseAutomationUrl}${pbjPath.PBJ_EXPORT}`;

  const params: Record<string, unknown> = {
    enterprise_name: realm,
    year: request.year,
    quarter: request.quarter,
    format: request.format,
  };

  //  Added only when a scope was actually requested. Not `facility_uuid: request.facilityUuid`
  //  with an undefined value: the service reads the parameter's *presence* to mean "one building",
  //  and axios would still be free to serialise a present-but-empty key.
  if (request.facilityUuid) {
    params.facility_uuid = request.facilityUuid;
  }

  return axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      ...(await getClientIpHeader()),
    },
    params,
    responseType: 'arraybuffer',
    timeout: PBJ_REQUEST_TIMEOUT_MS,
  });
};
